import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
    Button, CircularProgress, TextField, Switch,
    DialogActions, DialogContent, DialogContentText, DialogTitle
} from '@material-ui/core';
import { Card } from 'react-bootstrap';
import SaveIcon from '@material-ui/icons/Save';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import CloseIcon from '@material-ui/icons/Close';
import { Dialog } from 'components/shared/Dialog';
import ConfigItem from './ConfigItem';

const ReminderConfig = ({ config, onSubmit, onDelete, loading }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);

    const { control, handleSubmit, reset, formState: { errors } } = useForm({
        defaultValues: {
            enabled: true,
            maxNotifications: 3,
            intervalDays: 7,
            startDate: '',
        }
    });

    useEffect(() => {
        if (config) {
            reset({
                enabled: config.enabled ?? true,
                maxNotifications: config.maxNotifications ?? 3,
                intervalDays: config.intervalDays ?? 7,
                startDate: config.startDate ? config.startDate.substring(0, 10) : '',
            });
        } else {
            reset({ enabled: true, maxNotifications: 3, intervalDays: 7, startDate: '' });
        }
    }, [config, reset]);

    const handleEdit = () => setIsEditing(true);

    const handleCancel = () => {
        setIsEditing(false);
        if (config) {
            reset({
                enabled: config.enabled ?? true,
                maxNotifications: config.maxNotifications ?? 3,
                intervalDays: config.intervalDays ?? 7,
                startDate: config.startDate ? config.startDate.substring(0, 10) : '',
            });
        } else {
            reset({ enabled: true, maxNotifications: 3, intervalDays: 7, startDate: '' });
        }
    };

    const onFormSubmit = (data) => {
        onSubmit({
            enabled: data.enabled,
            maxNotifications: Number(data.maxNotifications),
            intervalDays: Number(data.intervalDays),
            startDate: data.startDate ? `${data.startDate}T00:00:00Z` : undefined,
        }, () => setIsEditing(false));
    };

    const handleDeleteConfirm = () => {
        setConfirmOpen(false);
        onDelete();
    };

    return (
        <>
        <Card className='p-4 mt-4 mb-3'>
            <form onSubmit={handleSubmit(onFormSubmit)}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                    <h5 style={{ margin: 0 }}>Reminder Configuration</h5>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        {isEditing ? (
                            <>
                                <Button onClick={handleCancel} disabled={loading} startIcon={<CloseIcon />}>
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    disabled={loading}
                                    startIcon={loading ? <CircularProgress size={16} /> : <SaveIcon />}
                                >
                                    Save
                                </Button>
                            </>
                        ) : config ? (
                            <>
                                <Button variant="contained" color="primary" startIcon={<EditIcon />} onClick={handleEdit}>
                                    Edit Configuration
                                </Button>
                                <Button variant="outlined" color="secondary" startIcon={<DeleteIcon />} onClick={() => setConfirmOpen(true)}>
                                    Delete
                                </Button>
                            </>
                        ) : (
                            <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleEdit}>
                                Create Configuration
                            </Button>
                        )}
                    </div>
                </div>

                {isEditing ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        <Controller
                            name="enabled"
                            control={control}
                            render={({ field }) => (
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: '12px 16px',
                                    backgroundColor: 'var(--bg-canvas)',
                                    borderRadius: '8px',
                                }}>
                                    <span style={{ fontWeight: 500, color: '#333' }}>Enabled</span>
                                    <Switch
                                        checked={field.value}
                                        onChange={(e) => field.onChange(e.target.checked)}
                                        color="primary"
                                    />
                                </div>
                            )}
                        />
                        <Controller
                            name="maxNotifications"
                            control={control}
                            rules={{ required: 'Required', min: { value: 1, message: 'Must be at least 1' } }}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label="Max Notifications"
                                    type="number"
                                    variant="outlined"
                                    fullWidth
                                    error={!!errors.maxNotifications}
                                    helperText={errors.maxNotifications?.message}
                                    inputProps={{ min: 1 }}
                                />
                            )}
                        />
                        <Controller
                            name="intervalDays"
                            control={control}
                            rules={{ required: 'Required', min: { value: 1, message: 'Must be at least 1' } }}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label="Interval Days"
                                    type="number"
                                    variant="outlined"
                                    fullWidth
                                    error={!!errors.intervalDays}
                                    helperText={errors.intervalDays?.message}
                                    inputProps={{ min: 1 }}
                                />
                            )}
                        />
                        <Controller
                            name="startDate"
                            control={control}
                            rules={{ required: 'Required' }}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label="Start Date"
                                    type="date"
                                    variant="outlined"
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    error={!!errors.startDate}
                                    helperText={errors.startDate?.message}
                                />
                            )}
                        />
                    </div>
                ) : config ? (
                    <div style={{ backgroundColor: 'var(--bg-canvas)', borderRadius: '8px', padding: '8px 16px' }}>
                        <ConfigItem label="Enabled" value={config.enabled} />
                        <ConfigItem label="Max Notifications" value={config.maxNotifications} />
                        <ConfigItem label="Interval Days" value={config.intervalDays} />
                        <ConfigItem label="Start Date" value={config.startDate ? new Date(config.startDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : null} />
                    </div>
                ) : (
                    <div style={{ color: '#999', textAlign: 'center', padding: '32px 0' }}>
                        No reminder configuration set up yet.
                    </div>
                )}
            </form>
        </Card>

        <Dialog open={confirmOpen} fullWidth maxWidth="xs">
            <DialogTitle>Delete Reminder Configuration</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    Are you sure you want to delete the reminder configuration? This action cannot be undone.
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
                <Button variant="contained" color="secondary" onClick={handleDeleteConfirm}>Delete</Button>
            </DialogActions>
        </Dialog>
        </>
    );
};

export default ReminderConfig;
